import React from 'react'
import { graphql } from 'gatsby'
import Seo from '../../components/seo'
import get from 'lodash/get'
import Layout from '../../components/layout'
import { Grid, Link, Typography } from '@material-ui/core'
import Pagination from '@material-ui/lab/Pagination'
import PaginationItem from '@material-ui/lab/PaginationItem'
import styles from './index.module.css'
import News from '../../components/news'
import BrandedHero from '../../components/branded-hero'
import BrandedMedia from '../../components/branded-media'
import { isHealthContent } from '../../utils/content-categories'
import InformationNews from '../../components/information-news'
import { mergeEditorialArticles } from '../../utils/editorial-articles'

const categoryDescription = (category) => {
  if (category === 'Sanità') {
    return 'Salute dei cittadini, prevenzione, servizio sanitario e attività del Ministero della Salute, raccolti in un unico spazio.'
  }
  return `Notizie e approfondimenti MRF dedicati a ${category.toLowerCase()}, ordinati per data.`
}

class CategoriaTemplate extends React.Component {
  render() {
    const categoria = get(this.props, 'pageContext.label')
    const pageTitle = `Movimento Rivoluzione Fiscale - ${categoria}`

    const slug = get(this.props, 'pageContext.slug')
    const page = get(this.props, 'pageContext.page')
    const allPosts = mergeEditorialArticles(get(this.props, 'data.allContentfulArticolo.edges').map(
      (post) => post.node
    ))
    const allVideos = get(
      this.props,
      'data.allContentfulVideo.edges',
      []
    ).map((video) => video.node)
    const matchingVideos = allVideos.filter((video) => {
      const assigned = (video.categorie || []).some(
        (categoriaVideo) => categoriaVideo.label === categoria
      )
      if (categoria === 'Sanità') return isHealthContent(video)
      if (isHealthContent(video)) return false
      return assigned
    })
    const matchingPosts = allPosts.filter((post) => {
      const categorieAssegnate = (post.categorie || []).map(
        (categoriaArticolo) => categoriaArticolo.label
      )
      if (categoria === 'Sanità') {
        return isHealthContent(post)
      }
      if (isHealthContent(post)) return false
      return categorieAssegnate.includes(categoria)
    })
    const total = matchingPosts.length
    const maxPages = Math.max(1, Math.ceil(Math.max(total - 4, 0) / 5))
    const postsFixed = matchingPosts.slice(0, 4)
    const paginationStart = 4 + (page - 1) * 5
    const postsPagination = matchingPosts.slice(
      paginationStart,
      paginationStart + 5
    )
    const handleChange = (event, value) => {
      window.location.href = `/${slug}/${value}/`
    }

    return (
      <Layout location={this.props.location}>
        <Seo title={`${categoria} | MRF${page > 1 ? ` · Pagina ${page}` : ''}`} description={categoryDescription(categoria) + (page > 1 ? ` Pagina ${page}.` : '')} path={`/${slug}/${page}/`} noIndex={page > maxPages} />
        <div className={styles.container}>
          <div className={styles.gridContainer}>
            <Grid container spacing={0}>
              <Grid item xs={12} className={styles.titleContainer}>
                <Typography variant={'h2'} component="h1">{categoria}</Typography>
                <Typography className={styles.intro}>
                  {categoryDescription(categoria)}
                </Typography>
              </Grid>
              {page === 1 && (
                <Grid item xs={12}>
                  <InformationNews category={categoria} />
                </Grid>
              )}
              {matchingVideos.length > 0 && (
                <Grid item xs={12} className={styles.featuredBlock}>
                  <News
                    value={0}
                    index={0}
                    news={matchingVideos.slice(0, 2)}
                  />
                </Grid>
              )}
              {postsFixed.length > 0 && (
                <Grid item xs={12} className={styles.featuredBlock}>
                  <News value={0} index={0} news={postsFixed.slice(0, 4)} />
                </Grid>
              )}
              {postsPagination.map((post) => (
                <Grid
                  item
                  container
                  xs={12}
                  className={styles.articleRow}
                  key={`article-list-${post.id}`}
                >
                  <Grid item xs={12} md={5} lg={4}>
                    {post.heroImage ? (
                      <Link href={`/articoli/${post.slug}/`}>
                        <BrandedHero
                          className={styles.paginatedImage}
                          alt={post.titolo}
                          src={post.heroImage.file.url}
                          brandingKey={post.slug}
                        />
                      </Link>
                    ) : post.embed ? (
                      <BrandedMedia
                        className={styles.paginatedMedia}
                        html={post.embed.embed}
                        brandingKey={post.slug}
                      />
                    ) : null}
                  </Grid>
                  <Grid item xs={12} md={7} lg={8} className={styles.articleText}>
                    <Link
                      href={
                        post.autore
                          ? `/articoli/${post.slug}/`
                          : post.embed
                          ? `/video/${post.slug}/`
                          : post.etichetta && post.url
                          ? post.url
                          : null
                      }
                    >
                      <Typography
                        variant={'h3'}
                        className={styles.titleArticle}
                      >
                        {post.titolo}
                      </Typography>
                    </Link>
                    {post.autore && (
                      <Typography
                        variant={'subtitle1'}
                        className={styles.author}
                      >
                        {`${post.autore.nome} ${post.autore.cognome}, ${post.dataPubblicazione}`}
                      </Typography>
                    )}
                    {post.contenuto && post.contenuto.childMarkdownRemark && (
                      <div
                        className={styles.content}
                        dangerouslySetInnerHTML={{
                          __html: post.contenuto.childMarkdownRemark.html,
                        }}
                      />
                    )}
                  </Grid>
                </Grid>
              ))}
            </Grid>
            <Pagination
              classes={{
                root: styles.paginationRoot,
              }}
              renderItem={(item) => (
                <PaginationItem
                  component={item.page && !item.disabled ? 'a' : 'button'}
                  href={item.page && !item.disabled ? `/${slug}/${item.page}/` : undefined}
                  classes={{
                    selected: styles.paginationItemSelected,
                  }}
                  {...item}
                />
              )}
              count={maxPages}
              page={page}
              onChange={handleChange}
              variant="outlined"
              shape="rounded"
            />
          </div>
        </div>
      </Layout>
    )
  }
}

export default CategoriaTemplate

export const pageQuery = graphql`
  query ArticoliPerCategoria {
    site {
      siteMetadata {
        title
      }
    }
    allContentfulVideo(
      filter: { slug: { ne: "non-eliminare" }, titolo: { ne: "NON ELIMINARE" } }
      sort: { fields: [dataPubblicazione], order: DESC }
    ) {
      edges {
        node {
          id
          slug
          titolo
          categorie {
            label
            slug
          }
          contenuto {
            childMarkdownRemark {
              html
            }
          }
          dataPubblicazione
          embed {
            embed
          }
        }
      }
    }
    allContentfulArticolo(
      filter: { slug: { ne: "non-eliminare" }, titolo: { ne: "NON ELIMINARE" } }
      sort: { fields: [dataPubblicazione], order: DESC }
    ) {
      edges {
        node {
          id
          slug
          titolo
          categorie {
            label
            slug
          }
          contenuto {
            childMarkdownRemark {
              html
            }
          }
          autore {
            cognome
            nome
            descrizione
          }
          publishedAt: dataPubblicazione
          dataPubblicazione(formatString: "MMMM Do, YYYY")
          heroImage {
            file {
              url
            }
            fluid(maxWidth: 1600, quality: 85, background: "rgb:000000") {
              ...GatsbyContentfulFluid
            }
          }
        }
      }
    }
  }
`

